<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

import { usePagination } from '@/composables/usePagination'
import { useSearch } from '@/composables/useSearch'
import {
  getBannerPage,
  getBannerDetail,
  createBanner,
  updateBanner,
  deleteBanner,
  updateBannerStatus
} from '@/api/banner'

import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import BannerFormModal from './components/BannerFormModal.vue'

// 数据
const loading = ref(false)
const bannerList = ref([])

// 分页
const { pagination, goToPage, setPageSize, updatePagination, getPaginationParams } = usePagination({ pageSize: 10 })

// 搜索
const { searchForm, resetSearch, getSearchParams } = useSearch({
  initialForm: {
    position: '',
    status: '',
    platform: ''
  },
  onSearch: fetchData
})

// 表单窗口
const showFormModal = ref(false)
const isEdit = ref(false)
const editBannerData = ref(null)
const formModalRef = ref()

// 位置选项
const positionOptions = [
  { label: '全部位置', value: '' },
  { label: '首页', value: 'home' },
  { label: '分类页', value: 'category' }
]

// 状态选项
const statusOptions = [
  { label: '全部状态', value: '' },
  { label: '草稿', value: 0 },
  { label: '已上线', value: 1 },
  { label: '已下线', value: 2 }
]

// 平台选项
const platformOptions = [
  { label: '全部平台', value: '' },
  { label: '微信小程序', value: 1 },
  { label: '支付宝小程序', value: 2 },
  { label: '抖音小程序', value: 3 },
  { label: '快手小程序', value: 4 },
  { label: 'iOS', value: 5 },
  { label: 'Android', value: 6 },
  { label: 'H5', value: 7 }
]

// 状态配置
const statusConfig = {
  0: { label: '草稿', dot: 'bg-slate-400', bg: 'bg-slate-100', text: 'text-slate-700' },
  1: { label: '已上线', dot: 'bg-green-500', bg: 'bg-green-100', text: 'text-green-700' },
  2: { label: '已下线', dot: 'bg-red-400', bg: 'bg-red-100', text: 'text-red-700' }
}

// 平台名称映射
const platformMap = {
  1: '微信', 2: '支付宝', 3: '抖音', 4: '快手', 5: 'iOS', 6: 'Android', 7: 'H5'
}

// 位置名称映射
const positionMap = {
  home: '首页',
  category: '分类页'
}

// 获取数据
async function fetchData() {
  loading.value = true
  try {
    const params = {
      ...getSearchParams(),
      ...getPaginationParams()
    }
    const data = await getBannerPage(params)
    bannerList.value = data.records || []
    updatePagination(data)
  } catch (error) {
    ElMessage.error('加载Banner列表失败')
    console.error('加载Banner列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 筛选变化
const handleFilterChange = () => {
  pagination.current = 1
  fetchData()
}

// 换页
const changePage = (page) => {
  goToPage(page)
  fetchData()
}

// 切换每页条数
const changePageSize = (size) => {
  setPageSize(size)
  fetchData()
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return '-'
  return time.replace('T', ' ').substring(0, 16)
}

// ========== 操作 ==========

// 新增
const handleAdd = () => {
  isEdit.value = false
  editBannerData.value = null
  showFormModal.value = true
}

// 编辑
const handleEdit = async (item) => {
  try {
    const detail = await getBannerDetail(item.id)
    isEdit.value = true
    editBannerData.value = detail
    showFormModal.value = true
  } catch (error) {
    ElMessage.error('获取Banner详情失败')
  }
}

// 表单提交
const handleFormConfirm = async (data) => {
  try {
    if (isEdit.value) {
      await updateBanner(data.id, data)
      ElMessage.success('更新Banner成功')
    } else {
      await createBanner(data)
      ElMessage.success('创建Banner成功')
    }
    formModalRef.value?.done()
    fetchData()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
    formModalRef.value?.done()
  }
}

// 状态切换
const handleStatusChange = async (item, targetStatus) => {
  const statusText = { 0: '设为草稿', 1: '上线', 2: '下线' }
  try {
    await ElMessageBox.confirm(
      `确定要将此Banner${statusText[targetStatus]}吗？`,
      '确认操作',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    await updateBannerStatus(item.id, targetStatus)
    ElMessage.success('状态更新成功')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.message || '状态更新失败')
    }
  }
}

// 删除
const handleDelete = async (item) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除此Banner吗？删除后无法恢复。',
      '确认删除',
      {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    await deleteBanner(item.id)
    ElMessage.success('删除成功')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.message || '删除失败')
    }
  }
}

onMounted(() => {
  fetchData()
})
</script>

<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
    <!-- 页面标题 -->
      <div class="list-page-heading-main">
        <h1 class="text-xl font-semibold text-slate-900">Banner管理</h1>
        <p class="text-sm text-slate-500 mt-1">管理各端应用的轮播图配置</p>
      </div>
      <div class="list-page-heading-actions">
        <button
          v-permission="'app:banner:manage'"
          @click="handleAdd"
          class="btn-primary inline-flex items-center gap-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          新增Banner
        </button>
      </div>

    <!-- 筛选栏 -->
        </div>
      </div>
    </template>

    <!-- 表格 -->
    <div class="list-page-table-panel">
      <PageFilterBar
        shell-class="filter-bar-shell is-embedded"
        body-class="filter-bar-body is-panel"
        :show-refresh="true"
        :refresh-loading="loading"
        @refresh="fetchData"
      >
        <el-select
          v-model="searchForm.position"
          placeholder="请选择位置"
          class="form-select form-control-sm filter-bar-field"
          @change="handleFilterChange"
        >
          <el-option v-for="opt in positionOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>

        <el-select
          v-model="searchForm.status"
          placeholder="请选择状态"
          class="form-select form-control-sm filter-bar-field"
          @change="handleFilterChange"
        >
          <el-option v-for="opt in statusOptions" :key="String(opt.value)" :label="opt.label" :value="opt.value" />
        </el-select>

        <el-select
          v-model="searchForm.platform"
          placeholder="请选择平台"
          class="form-select form-control-sm filter-bar-field"
          @change="handleFilterChange"
        >
          <el-option v-for="opt in platformOptions" :key="String(opt.value)" :label="opt.label" :value="opt.value" />
        </el-select>

        <template #searchActions>
          <button
            @click="fetchData"
            class="btn-primary"
          >
            搜索
          </button>
          <button
            @click="resetSearch"
            class="btn-secondary"
          >
            重置
          </button>
        </template>
      </PageFilterBar>

      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th w-20">图片</th>
                <th class="data-table-th">标题</th>
                <th class="data-table-th is-center w-20">位置</th>
                <th class="data-table-th is-center w-20">排序</th>
                <th class="data-table-th is-center w-20">状态</th>
                <th class="data-table-th">平台</th>
                <th class="data-table-th w-36">生效时间</th>
                <th class="data-table-th w-36">创建时间</th>
                <th class="data-table-th is-center">操作</th>
              </tr>
            </thead>
            <tbody class="data-table-body">
              <!-- 加载骨架屏 -->
              <template v-if="loading">
                <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td class="data-table-td"><div class="h-10 w-16 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td"><div class="h-4 bg-slate-200 rounded w-32"></div></td>
                  <td class="data-table-td is-center"><div class="h-4 bg-slate-200 rounded w-12 mx-auto"></div></td>
                  <td class="data-table-td is-center"><div class="h-4 bg-slate-200 rounded w-8 mx-auto"></div></td>
                  <td class="data-table-td is-center"><div class="h-5 bg-slate-200 rounded w-14 mx-auto"></div></td>
                  <td class="data-table-td"><div class="h-4 bg-slate-200 rounded w-24"></div></td>
                  <td class="data-table-td"><div class="h-4 bg-slate-200 rounded w-28"></div></td>
                  <td class="data-table-td"><div class="h-4 bg-slate-200 rounded w-28"></div></td>
                  <td class="data-table-td is-center"><div class="h-4 bg-slate-200 rounded w-20 mx-auto"></div></td>
                </tr>
              </template>

              <!-- 空状态 -->
              <tr v-if="!loading && bannerList.length === 0">
                <td colspan="9" class="data-table-empty">
                  <div class="flex flex-col items-center">
                    <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                      <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                    <p class="text-slate-500 text-sm mb-1">暂无Banner数据</p>
                    <p class="text-slate-400 text-xs">点击右上角按钮新增Banner</p>
                  </div>
                </td>
              </tr>

              <!-- 数据行 -->
              <template v-if="!loading">
                <tr v-for="item in bannerList" :key="item.id" class="data-table-row">
                  <!-- 图片 -->
                  <td class="data-table-td">
                    <img
                      v-if="item.imageUrl"
                      :src="item.imageUrl"
                      :alt="item.title"
                      class="h-10 w-16 object-cover rounded border border-slate-200"
                    />
                    <div v-else class="h-10 w-16 bg-slate-100 rounded border border-slate-200 flex items-center justify-center">
                      <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                  </td>

                  <!-- 标题 -->
                  <td class="data-table-td">
                    {{ item.title || '-' }}
                  </td>

                  <!-- 位置 -->
                  <td class="data-table-td is-center">
                    {{ positionMap[item.position] || item.position || '-' }}
                  </td>

                  <!-- 排序 -->
                  <td class="data-table-td is-center">
                    {{ item.sort ?? '-' }}
                  </td>

                  <!-- 状态 -->
                  <td class="data-table-td is-center">
                    <span
                      class="status-badge"
                      :class="[statusConfig[item.status]?.bg, statusConfig[item.status]?.text]"
                    >
                      <span class="w-1.5 h-1.5 rounded-full mr-1.5" :class="statusConfig[item.status]?.dot"></span>
                      {{ statusConfig[item.status]?.label || '未知' }}
                    </span>
                  </td>

                  <!-- 平台 -->
                  <td class="data-table-td">
                    <div class="flex items-center gap-1 flex-wrap">
                      <span
                        v-for="config in (item.platformConfigs || [])"
                        :key="config.id"
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-600"
                      >
                        {{ platformMap[config.platform] || config.platform }}
                      </span>
                      <span v-if="!item.platformConfigs?.length" class="text-xs text-slate-400">-</span>
                    </div>
                  </td>

                  <!-- 生效时间 -->
                  <td class="data-table-td">
                    <template v-if="item.startTime || item.endTime">
                      <div class="text-xs">{{ formatTime(item.startTime) }}</div>
                      <div class="text-xs">{{ formatTime(item.endTime) }}</div>
                    </template>
                    <span v-else>-</span>
                  </td>

                  <!-- 创建时间 -->
                  <td class="data-table-td">
                    <span class="text-xs">{{ formatTime(item.createTime) }}</span>
                  </td>

                  <!-- 操作 -->
                  <td class="data-table-td is-center">
                    <div class="flex items-center justify-center gap-1">
                      <!-- 编辑 -->
                      <button
                        v-permission="'app:banner:manage'"
                        @click="handleEdit(item)"
                        class="table-action-button is-primary"
                        title="编辑" aria-label="编辑"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                      </button>

                      <!-- 上线（草稿或已下线可上线） -->
                      <button
                        v-permission="'app:banner:manage'"
                        v-if="item.status === 0 || item.status === 2"
                        @click="handleStatusChange(item, 1)"
                        class="table-action-button is-success"
                        title="上线" aria-label="上线"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
                        </svg>
                      </button>

                      <!-- 下线（已上线可下线） -->
                      <button
                        v-permission="'app:banner:manage'"
                        v-if="item.status === 1"
                        @click="handleStatusChange(item, 2)"
                        class="table-action-button is-teal"
                        title="下线" aria-label="下线"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                        </svg>
                      </button>

                      <!-- 删除 -->
                      <button
                        v-permission="'app:banner:manage'"
                        @click="handleDelete(item)"
                        class="table-action-button is-danger"
                        title="删除" aria-label="删除"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 分页 -->
      <AppPagination
        v-if="pagination.total > 0"
        class="pagination-bridge"
        :pagination="pagination"
        :page-sizes="[10, 20, 50]"
        @page-change="changePage"
        @size-change="changePageSize"
      />
    </div>

    <!-- 新增/编辑窗口 -->
    <BannerFormModal
      ref="formModalRef"
      v-model:show="showFormModal"
      :is-edit="isEdit"
      :banner-data="editBannerData"
      @confirm="handleFormConfirm"
    />
  </ListPageShell>
</template>
